import type { EvidenceFact } from "@digital-twins/shared-types";

/**
 * Shared numbering seam for analyst fact citations.
 *
 * The inline chips in `SafeAnalystMarkdown` and the rows in `AnalystEvidence`
 * must agree on every number, so neither computes its own. `AnalystMessage`
 * builds this once per message and hands the same object to both.
 *
 * The marker written into answer text is the fact id in brackets — the model is
 * told to cite `[fact-id]` (see `prompts.py`), and `_fact_id` in the python
 * service produces ids that already carry the `fact-` prefix. So the text
 * between the brackets IS the `factId`, with no further parsing.
 */

/** Matches one `[fact-…]` marker; group 1 is the bare factId. */
export const FACT_CITATION_RE = /\[(fact-[A-Za-z0-9._:-]+)\]/g;

/**
 * A marker plus the whitespace in front of it. Used when a marker is removed
 * rather than rendered, so a stripped citation does not leave a double space
 * before the sentence's full stop.
 */
export const FACT_CITATION_WITH_LEADING_SPACE_RE =
  /\s*\[fact-[A-Za-z0-9._:-]+\]/g;

/**
 * One cited breakdown is one source.
 *
 * Crosstab cells already share `custom-analysis-{run}`. Demographic buckets
 * are stored as `respondent-attribute.{column}.{value}`; those values are one
 * attribute. Other facts stay on their own source id.
 */
const RESPONDENT_ATTRIBUTE_VALUE = /^respondent-attribute\.([^.]+)\..+/;

export function evidenceSourceKey(fact: EvidenceFact): string {
  const sourceId = fact.source.sourceId;
  const attribute = RESPONDENT_ATTRIBUTE_VALUE.exec(sourceId);
  if (attribute) {
    return `respondent-attribute.${attribute[1]}`;
  }
  return sourceId;
}

/**
 * Localized names for the two fact kinds whose server labels are internal:
 * a respondent fact's labels carry the respondent id ("Scored respondent
 * r-3", "r-3 score") and a quote's source label is a provenance tag. Neither
 * is ever shown; these generic names stand in.
 */
export interface EvidenceKindLabels {
  respondent: string;
  verbatim: string;
}

/** The display name of one fact's source: never a respondent id or a provenance tag. */
export function factSourceLabel(
  fact: EvidenceFact,
  kindLabels: EvidenceKindLabels,
): string {
  if (fact.kind === "respondent") {
    return kindLabels.respondent;
  }
  if (fact.kind === "quote") {
    return kindLabels.verbatim;
  }
  return fact.source.label;
}

/**
 * The display label of one fact. A respondent is named by its twin, else
 * generically; a quote keeps its question title, unless the label is only
 * the source tag repeated (the server's fallback when no question is known).
 */
export function factDisplayLabel(
  fact: EvidenceFact,
  kindLabels: EvidenceKindLabels,
): string {
  if (fact.kind === "respondent") {
    return fact.personaName?.trim() || kindLabels.respondent;
  }
  if (fact.kind === "quote" && fact.label === fact.source.label) {
    return kindLabels.verbatim;
  }
  return fact.label;
}

export function evidenceGroupTitle(
  facts: Array<EvidenceFact>,
  kindLabels: EvidenceKindLabels,
): string {
  const sourceLabels = new Set(
    facts.map((fact) => factSourceLabel(fact, kindLabels)),
  );
  if (sourceLabels.size === 1) {
    return factSourceLabel(facts[0], kindLabels);
  }
  const heads = facts.map((fact) => {
    const label = factDisplayLabel(fact, kindLabels);
    const separator = label.indexOf(": ");
    return separator === -1 ? label : label.slice(0, separator);
  });
  if (new Set(heads).size === 1) {
    return heads[0];
  }
  return factSourceLabel(facts[0], kindLabels);
}

export interface FactNumbering {
  /**
   * factId to its 1-based display number. Only facts cited in the visible
   * answer (and present in evidence) are numbered, in first-appearance order.
   * A chip and its sources-list row both read this map.
   */
  numberByFactId: Map<string, number>;
  /** Resolvable factIds cited in the visible text, in first-appearance order. */
  placedFactIds: Array<string>;
  /** factIds present in evidence but never cited in the visible text. */
  unplacedFactIds: Array<string>;
}

/**
 * Build the numbering for one message.
 *
 * Two rules carry the behavior the plan pins down:
 *
 * 1. A factId cited more than once reuses its first number. The model routinely
 *    cites the same fact in a headline and again in a bullet.
 * 2. A cited id with no matching evidence entry is left OUT of the map entirely,
 *    which is the signal to callers that the marker must be stripped rather than
 *    rendered. Unresolvable ids genuinely reach the client: `audit.py` filters
 *    cited ids against known facts and `normalize_evidence_citations` only
 *    resolves truncations that are unique, so a hallucinated or ambiguous id
 *    survives into the answer text. Rendering a chip that points at nothing
 *    would turn today's silent strip into a visible defect.
 */
export function buildFactNumbering(
  content: string,
  evidence: Array<EvidenceFact>,
): FactNumbering {
  const knownFactIds = new Set(evidence.map((fact) => fact.factId));

  const placedFactIds: Array<string> = [];
  const seen = new Set<string>();

  // `matchAll` needs the regex to stay global; a shared literal would carry
  // `lastIndex` between calls, so the pattern is rebuilt per invocation.
  // Caveats travel typed beside the reply (`message.caveats`), never in
  // `content`, so every citation here is in the visible answer.
  const matches = content.matchAll(
    new RegExp(FACT_CITATION_RE.source, "g"),
  );
  for (const match of matches) {
    const factId = match[1];
    if (!knownFactIds.has(factId) || seen.has(factId)) {
      continue;
    }
    seen.add(factId);
    placedFactIds.push(factId);
  }

  // Deduped: the same `factId` can appear twice in `evidence` (two tool
  // results describing one underlying fact).
  const unplacedFactIds: Array<string> = [];
  const unplacedSeen = new Set<string>();
  for (const fact of evidence) {
    if (seen.has(fact.factId) || unplacedSeen.has(fact.factId)) {
      continue;
    }
    unplacedSeen.add(fact.factId);
    unplacedFactIds.push(fact.factId);
  }

  // Facts from one breakdown share a number. A chip and its sources row both
  // read this map, so a six-bucket age split is source 4, not sources 4–9.
  const sourceKeyByFactId = new Map(
    evidence.map((fact) => [fact.factId, evidenceSourceKey(fact)]),
  );
  const numberBySourceKey = new Map<string, number>();
  const numberByFactId = new Map<string, number>();
  for (const factId of placedFactIds) {
    const sourceKey = sourceKeyByFactId.get(factId) ?? factId;
    const existing = numberBySourceKey.get(sourceKey);
    if (existing !== undefined) {
      numberByFactId.set(factId, existing);
      continue;
    }
    const number = numberBySourceKey.size + 1;
    numberBySourceKey.set(sourceKey, number);
    numberByFactId.set(factId, number);
  }

  return { numberByFactId, placedFactIds, unplacedFactIds };
}

/**
 * Whether a cited marker should render a chip. False means the marker is
 * unresolvable and the caller strips it, exactly as the tab has always done.
 */
export function isRenderableFactId(
  factId: string,
  numbering: FactNumbering,
): boolean {
  return numbering.numberByFactId.has(factId);
}
